import { getCallLog, getEsTrendingTopics, getTrendingTopics, isElasticSearchEnabled } from '@/services/call-log-service/call-log-service';
import {
  CallLogFilters,
  CallRow,
  CallsResponse,
  TrendingTopicsParams,
} from '@/services/call-log-service/call-log-service.types';
import { ReactQueryKeys } from '@/utils/react-query-keys';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { OrderBy } from '../use-sort-and-pagination/use-sort-and-pagination.types';
import { useWebsocket } from '../use-websocket/use-websocket';

interface UseCallLogProps {
  filtersParams: CallLogFilters;
  paginationParams: {
    sort_by?: string;
    order_by: OrderBy;
    page: number;
    items_per_page: number;
  };
  /** How many pages the caller has on screen. Defaults to 1, which is the numbered-pager
   *  case. Only used to decide whether an inbound call should trigger a refetch. */
  loadedPages?: number;
}

/** Key-order independent, because JSON.stringify is not. */
const sortedEntries = (value: Record<string, unknown>) =>
  Object.entries(value).sort(([a], [b]) => a.localeCompare(b));

/**
 * The request key with the row count dropped: [name, params, backend], where params is the
 * only segment that carries items_per_page. Two keys that stringify the same differ in how
 * many rows were asked for and in nothing else, so filters, sort, search and backend must
 * all match.
 */
const keyWithoutRowCount = (key: readonly unknown[]) => {
  const [name, params, backend] = key;
  const { items_per_page: _rows, ...rest } = (params ?? {}) as Record<string, unknown>;
  return JSON.stringify([name, sortedEntries(rest), backend]);
};

const isRowCountOnlyChange = (previous: readonly unknown[] | undefined, next: unknown[]) =>
  previous !== undefined && keyWithoutRowCount(previous) === keyWithoutRowCount(next);

export const useCallLog = ({
  filtersParams,
  paginationParams,
  loadedPages = 1,
}: UseCallLogProps) => {
  const queryClient = useQueryClient();
  const searchBackend = isElasticSearchEnabled() ? 'es' : 'pg';
  const requestKey = [
    ...ReactQueryKeys.CALL_LOG({ ...filtersParams, ...paginationParams }),
    searchBackend,
  ];

  const {
    data: callLogData,
    error,
    isPending,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: requestKey,
    queryFn: () => getCallLog({ ...filtersParams, ...paginationParams }),
    staleTime: Infinity,
    // Only across a row-count change. Loading more rows is a new key, and without this the
    // rows on screen vanish while it resolves. Reusing it for every key change would report
    // success for a filter or sort change too, so the table would show the old rows with no
    // spinner and a stale count, which is indistinguishable from a finished result.
    placeholderData: (previousData, previousQuery) =>
      isRowCountOnlyChange(previousQuery?.queryKey, requestKey) ? previousData : undefined,
  });

  const trendingTopicsParams: TrendingTopicsParams = {
    time_range: filtersParams.time_range,
    duration_min: filtersParams.duration_min,
    duration_max: filtersParams.duration_max,
    query: filtersParams.query,
    status: filtersParams.status,
    agent: filtersParams.agent,
    filters: filtersParams.filters,
    company_name: filtersParams.company_name,
    type: filtersParams.type,
    topic: filtersParams.topic,
  };

  const { data: trendingTopics, isPending: isPendingTrendingTopics } = useQuery({
    queryKey: [...ReactQueryKeys.TRENDING_TOPICS(trendingTopicsParams), searchBackend],
    queryFn: () => getTrendingTopics(trendingTopicsParams),
    staleTime: Infinity,
    enabled: !isElasticSearchEnabled(),
  });

  const { data: esTrendingTopicsData, isPending: isPendingEsTrendingTopics } = useQuery({
    queryKey: [...ReactQueryKeys.TRENDING_TOPICS(trendingTopicsParams), 'es-detailed'],
    queryFn: () => getEsTrendingTopics(trendingTopicsParams),
    staleTime: Infinity,
    enabled: isElasticSearchEnabled(),
  });

  const resolvedTrendingTopics = isElasticSearchEnabled()
    ? esTrendingTopicsData?.topics.map((t) => t.name)
    : trendingTopics;

  const resolvedIsPendingTrendingTopics = isElasticSearchEnabled()
    ? isPendingEsTrendingTopics
    : isPendingTrendingTopics;

  useWebsocket<{ call: CallRow }>('CallsChannel', ({ call: updatedCall }) => {
    const queryKey = [...ReactQueryKeys.CALL_LOG({ ...filtersParams, ...paginationParams }), searchBackend];
    const oldCalls = queryClient.getQueryData<CallsResponse>(queryKey);

    if (!oldCalls) return;

    const exists = oldCalls.calls.some((call) => call.id === updatedCall.id);

    if (exists) {
      queryClient.setQueryData(queryKey, (old: CallsResponse | undefined) => {
        if (!old) return old;
        return {
          ...old,
          calls: old.calls.map((call) =>
            call.id === updatedCall.id
              ? {
                  ...updatedCall,
                  // new Set(undefined) is an empty set, not a throw, so a frame that omits
                  // `topics` would silently blank the column instead of failing loudly.
                  topics: Array.isArray(updatedCall.topics)
                    ? [...new Set(updatedCall.topics)]
                    : call.topics,
                }
              : call
          ),
        };
      });
    } else if (loadedPages === 1) {
      // Only for someone still on the first page's worth of rows. `paginationParams.page` no
      // longer distinguishes that: callers accumulating rows send page 1 with a larger row
      // count, so testing it would refetch the whole accumulated list on every inbound call.
      refetch();
    }
  });

  return {
    callLogData,
    error,
    isPending,
    isFetching,
    refetch,
    trendingTopics: resolvedTrendingTopics,
    isPendingTrendingTopics: resolvedIsPendingTrendingTopics,
    esTrendingTopicsData,
  };
};
